import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const HomePage = () => {
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Alpha Consulting LLC',
    alternateName: ['The Alpha Consult', 'The Alpha Consulting'],
    url: 'https://thealphaconsult.com/',
    logo: 'https://thealphaconsult.com/logo.png',
    image: 'https://thealphaconsult.com/3ff0f777-6fef-48b9-81ec-9c3949d95efa_3840.png',
    telephone: '+1-770-545-1557',
    email: 'info@thealphaconsult.com',
    description: 'Atlanta, Georgia technology consulting company delivering product engineering, cloud solutions, custom software, and automation services.',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Atlanta',
      addressRegion: 'GA',
      addressCountry: 'US'
    },
    areaServed: [
      {
        '@type': 'City',
        name: 'Atlanta'
      },
      {
        '@type': 'State',
        name: 'Georgia'
      },
      {
        '@type': 'Country',
        name: 'United States'
      }
    ],
    sameAs: [
      'https://www.linkedin.com/company/alpha-consulting'
    ],
    knowsAbout: [
      'Custom software development',
      'Product engineering',
      'Cloud consulting',
      'SaaS development',
      'Application modernization',
      'Automation'
    ]
  };

  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  
  // Randomized metrics with positive trending
  const [metrics, setMetrics] = useState({
    onTimeDelivery: 95,
    leadTime: 12
  });

  // Generate random positive trending metrics on mount
  useEffect(() => {
    // On-time delivery: 93-98% (high is good)
    const onTimeDelivery = Math.floor(Math.random() * 6) + 93;
    
    // Lead time: 8-14 days (lower is better, but showing as positive trend)
    const leadTime = Math.floor(Math.random() * 7) + 8;
    
    setMetrics({ onTimeDelivery, leadTime });
  }, []);

  useEffect(() => {
    // Initialize Chart.js for mini chart
    if (chartRef.current && window.Chart) {
      const ctx = chartRef.current.getContext('2d');
      const gradient = ctx.createLinearGradient(0, 0, 0, 64);
      gradient.addColorStop(0, 'rgba(251, 191, 36, 0.35)');
      gradient.addColorStop(1, 'rgba(251, 191, 36, 0)');

      // Destroy existing chart if it exists
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }

      chartInstanceRef.current = new window.Chart(chartRef.current, {
        type: 'line',
        data: {
          labels: Array.from({ length: 12 }, (_, i) => i + 1),
          datasets: [{
            data: [6, 7, 6.5, 7.5, 6.8, 8, 7.6, 9.2, 8.4, 9.8, 9.2, 10.3],
            tension: 0.4,
            borderColor: '#F59E0B',
            backgroundColor: gradient,
            fill: true,
            pointRadius: 0
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false }, tooltip: { enabled: false } },
          scales: {
            x: { display: false },
            y: { display: false }
          },
          elements: {
            line: { borderWidth: 2 }
          }
        }
      });
    }

    // Initialize Lucide icons
    if (window.lucide) {
      window.lucide.createIcons();
    }

    // Cleanup function to destroy chart on unmount
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) {
      const y = element.getBoundingClientRect().top + window.pageYOffset - 72;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div>
      <SEO 
        title="The Alpha Consult | Alpha Consulting LLC"
        description="The Alpha Consult, also known as Alpha Consulting LLC, is an Atlanta, Georgia technology consulting company helping startups and enterprises build custom software, SaaS platforms, cloud systems, and automation solutions."
        keywords="The Alpha Consult, The Alpha Consulting, Alpha Consulting LLC, Atlanta technology consulting company, Georgia software company, custom software development Atlanta, cloud consulting Georgia, SaaS development company Georgia"
        url="https://thealphaconsult.com/"
        image="https://thealphaconsult.com/3ff0f777-6fef-48b9-81ec-9c3949d95efa_3840.png"
        structuredData={localBusinessSchema}
      />
      {/* Hero / Overview */}
      <section id="overview" className="relative overflow-hidden pt-24 sm:pt-28 pb-16">
        <div className="sm:px-6 lg:px-8 max-w-full mr-auto ml-auto pr-4 pl-4 relative">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-wider text-amber-300/90 font-nunito">
                The Alpha Consult | Atlanta, Georgia technology consulting
              </p>
              <h1 className="sm:text-5xl lg:text-6xl leading-tight text-4xl tracking-tight mt-4 font-nunito font-bold">
                The Alpha Consult builds reliable software for Georgia businesses
              </h1>
              <p className="mt-6 text-lg text-orange-300 max-w-xl font-nunito">
                Alpha Consulting LLC, doing business as The Alpha Consult, helps Atlanta and Georgia businesses design, build, and scale web platforms, mobile apps, cloud systems, and internal tools. From discovery to launch: architecture, UX, QA, automation, and ongoing delivery.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Link 
                  to="/contact"
                  className="shiny-cta focus:outline-none inline-flex items-center justify-center"
                >
                  <span className="font-nunito">Book a call</span>
                </Link>
                
                <button
                  onClick={() => scrollToSection('#curriculum')}
                  className="inline-flex items-center justify-center gap-2 hover:bg-white/15 transition-all hover:scale-[1.02] text-orange-100 bg-white/10 border-white/10 border rounded-full pt-3 pr-6 pb-3 pl-6 backdrop-blur-lg font-nunito"
                >
                  Our Services
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5">
                    <path d="M12 7v14"></path>
                    <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z"></path>
                  </svg>
                </button>
              </div>

              <div className="mt-8 flex items-center gap-6">
                <div className="flex -space-x-2">
                  <img src="https://images.unsplash.com/photo-1500649297466-74794c70acfc?w=320&q=80" alt="Client avatar" className="w-9 h-9 rounded-full border-2 border-black object-cover" />
                  <img src="https://images.unsplash.com/photo-1527980965255-d3b416303d12?w=320&q=80" alt="Client avatar" className="w-9 h-9 rounded-full border-2 border-black object-cover" />
                  <img src="https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=320&q=80" alt="Client avatar" className="w-9 h-9 rounded-full border-2 border-black object-cover" />
                  <div className="w-9 h-9 rounded-full border-2 border-black bg-amber-400 flex items-center justify-center text-black text-xs font-nunito">
                    120+
                  </div>
                </div>
                <div>
                  <p className="text-sm text-orange-100 font-nunito">120+ projects delivered</p>
                  <p className="text-sm text-orange-400 font-nunito">Startups to enterprise</p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-orange-900/40">
                <img
                  src="/3ff0f777-6fef-48b9-81ec-9c3949d95efa_3840.png"
                  alt="Delivery preview"
                  className="w-full h-[460px] object-cover"
                />
                
                {/* Stat Badge 1 */}
                <div className="bg-black/80 border-white/10 border rounded-xl p-4 absolute top-4 right-4 shadow-lg backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex bg-orange-800 rounded-full items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5 text-gray-200">
                        <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"></path>
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-orange-300 font-nunito">On‑time delivery</p>
                      <p className="text-lg text-white font-nunito">{metrics.onTimeDelivery}%</p>
                    </div>
                  </div>
                </div>

                {/* Stat Badge 2 with Chart */}
                <div className="absolute bottom-4 left-4 backdrop-blur-sm rounded-xl p-4 shadow-lg bg-black/80 border border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex bg-orange-800 rounded-full items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5 text-gray-300">
                        <path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2"></path>
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-orange-300 font-nunito">Avg. lead time</p>
                      <p className="text-lg text-white font-nunito">{metrics.leadTime} days</p>
                    </div>
                  </div>
                  <div className="mt-3 w-44 h-16">
                    <canvas ref={chartRef} aria-label="Delivery velocity chart" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Trusted Partners Section */}
          <div className="md:p-8 rounded-3xl mt-16 pt-6 pr-6 pb-6 pl-6">
            <div className="text-center">
              <p className="text-sm text-white/50 font-nunito">Trusted by teams worldwide</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section Placeholder */}
      <section id="curriculum" className="relative py-20 border-t border-orange-900 bg-gradient-to-b from-black to-orange-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-amber-300/80 font-nunito">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span> What we do
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-nunito font-bold">
              Complete Product engineering services and Security Compliance
            </h2>
            <p className="mt-3 text-orange-300 font-nunito">
              Whether you're launching a new product, modernizing legacy systems, or scaling for growth, our expert teams combine engineering excellence with secure, compliant practices to deliver software that drives real business outcomes.
            </p>
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-orange-900/40 p-6">
              <h3 className="text-xl font-semibold font-nunito">For Georgia startups</h3>
              <p className="mt-3 text-orange-300 font-nunito">
                Validate a product idea, ship an MVP, or add senior delivery capacity without losing speed.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-orange-900/40 p-6">
              <h3 className="text-xl font-semibold font-nunito">For Atlanta tech teams</h3>
              <p className="mt-3 text-orange-300 font-nunito">
                Extend your engineering organization with product-minded developers, cloud specialists, and QA support.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-orange-900/40 p-6">
              <h3 className="text-xl font-semibold font-nunito">For growing businesses</h3>
              <p className="mt-3 text-orange-300 font-nunito">
                Modernize legacy systems, improve automation, and create scalable digital platforms that support operations and revenue.
              </p>
            </div>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <Link
              to="/atlanta-software-development"
              className="rounded-2xl border border-white/10 bg-black/40 p-6 hover:border-amber-500/40 transition-all"
            >
              <p className="text-sm uppercase tracking-wider text-amber-300/80 font-nunito">Local page</p>
              <h3 className="mt-3 text-2xl font-semibold font-nunito">Custom software development in Atlanta</h3>
              <p className="mt-3 text-orange-300 font-nunito">
                Learn how we support Atlanta startups and growing companies with product delivery, internal tools, APIs, and digital platforms.
              </p>
            </Link>
            <Link
              to="/georgia-cloud-consulting"
              className="rounded-2xl border border-white/10 bg-black/40 p-6 hover:border-amber-500/40 transition-all"
            >
              <p className="text-sm uppercase tracking-wider text-amber-300/80 font-nunito">Local page</p>
              <h3 className="mt-3 text-2xl font-semibold font-nunito">Cloud consulting in Georgia</h3>
              <p className="mt-3 text-orange-300 font-nunito">
                Explore how we help Georgia teams modernize infrastructure, improve DevOps, and build scalable cloud foundations.
              </p>
            </Link>
          </div>
          
          <div className="mt-6 text-center">
            <Link 
              to="/services" 
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-orange-100 text-black hover:bg-orange-200 transition-all hover:scale-[1.02] font-nunito font-medium"
            >
              View All Services
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
                <path d="M5 12h14"></path>
                <path d="m12 5 7 7-7 7"></path>
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="enroll" className="relative py-20 border-t border-orange-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm uppercase tracking-wider text-amber-300/80 font-nunito">
            Serving Atlanta, Georgia and teams across the USA
          </p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-nunito font-bold">
            Ready to start your project?
          </h2>
          <p className="mt-3 text-orange-300 font-nunito max-w-2xl mx-auto">
            Let's discuss how we can help you build something amazing.
          </p>
          <div className="mt-8">
            <Link 
              to="/contact" 
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-orange-100 text-black hover:bg-orange-200 transition-all hover:scale-[1.02] font-nunito font-medium"
            >
              Get in Touch
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
                <path d="M5 12h14"></path>
                <path d="m12 5 7 7-7 7"></path>
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
